import {
  ICCPProductCatalogClient,
  IProductCatalogClient,
  OfferingSKU,
  OfferingSKUMap,
  ProductKey,
  ProductKeyMap
} from '@atlassiansox/asc-product-catalog';
import { AddonProduct } from './models';
import {
  convertCCPPricingPlansToHAMS,
  PricingPlans,
  sanitizePricingPlans
} from './utils';

export type ProductPricingStoreOptions = {
  readonly productCatalogClient: IProductCatalogClient;
  readonly ccpProductCatalogClient: ICCPProductCatalogClient;
};

export interface IProductPricingStore {
  getPricingPlans(productKey: ProductKey): Promise<PricingPlans>;
  getProducts(
    productKeys: ReadonlyArray<ProductKey>
  ): Promise<ProductKeyMap<PricingPlans>>;
  searchAddons(
    parentProductKey: string,
    query: string,
    limit: number
  ): Promise<AddonProduct[]>;
  getOfferings(
    offeringSKUs: ReadonlyArray<OfferingSKU>
  ): Promise<OfferingSKUMap<PricingPlans>>;
}

export class ProductPricingStore implements IProductPricingStore {
  private readonly productCatalogClient: IProductCatalogClient;
  private readonly ccpProductCatalogClient: ICCPProductCatalogClient;
  private readonly cache: Map<ProductKey, Promise<PricingPlans | null>>;

  public constructor({
    productCatalogClient,
    ccpProductCatalogClient
  }: ProductPricingStoreOptions) {
    this.productCatalogClient = productCatalogClient;
    this.ccpProductCatalogClient = ccpProductCatalogClient;
    this.cache = new Map();
  }

  public async getPricingPlans(productKey: ProductKey): Promise<PricingPlans> {
    const pricingPlans = (await this.getProducts([productKey]))[productKey];
    if (pricingPlans) {
      return pricingPlans;
    } else {
      throw new Error(
        `Could not find pricing plans for product key: ${productKey}`
      );
    }
  }

  public async getProducts(
    productKeys: ReadonlyArray<ProductKey>
  ): Promise<ProductKeyMap<PricingPlans>> {
    const uncachedProductKeys = productKeys.filter(this.isProductKeyUncached);

    if (uncachedProductKeys.length > 0) {
      this.fetchProducts(uncachedProductKeys);
      return this.getProducts(productKeys);
    } else {
      return this.getCachedProducts(productKeys);
    }
  }

  public async getOfferings(
    offeringSKUs: ReadonlyArray<OfferingSKU>
  ): Promise<OfferingSKUMap<PricingPlans>> {
    const uncachedOfferingSKUs = offeringSKUs.filter(
      this.isOfferingSKUUncached
    );

    if (uncachedOfferingSKUs.length > 0) {
      this.fetchOfferings(uncachedOfferingSKUs);
      return this.getOfferings(offeringSKUs);
    } else {
      return this.getCachedOfferings(offeringSKUs);
    }
  }

  public async searchAddons(
    parentProductKey: string,
    query: string,
    limit = 100
  ): Promise<AddonProduct[]> {
    const addons = await this.productCatalogClient.searchAddons({
      parentProductKey,
      query,
      limit
    });

    return addons.map((addon) => ({
      ...addon,
      pricing: sanitizePricingPlans(addon.pricing)
    }));
  }

  private fetchProducts(productKeys: ReadonlyArray<ProductKey>): void {
    const pricingPromise = this.productCatalogClient.getPricing({
      productKeys
    });
    productKeys.forEach((productKey) => {
      this.cache.set(
        productKey,
        new Promise(async (resolve) => {
          const pricing = await pricingPromise;
          const pricingPlans = pricing[productKey];
          if (pricingPlans) {
            resolve(sanitizePricingPlans(pricingPlans));
          } else {
            resolve(null);
          }
        })
      );
    });
  }

  private fetchOfferings(offeringSKUs: ReadonlyArray<OfferingSKU>): void {
    const pricingPromise = this.ccpProductCatalogClient.getOfferingsBySKU({
      offeringSKUs
    });

    offeringSKUs.forEach((offeringSKU) => {
      this.cache.set(
        offeringSKU,
        new Promise(
          async (resolve: (value: PricingPlans | null) => void, reject) => {
            const pricing = await pricingPromise;
            const offering = pricing[offeringSKU];

            let ccpPricingPlans;
            if (offering && offering.fields.defaultPricingPlans) {
              ccpPricingPlans = offering.fields.defaultPricingPlans.map(
                (ccpPPContent) => ccpPPContent.fields
              );
            }

            if (ccpPricingPlans && ccpPricingPlans.length > 0) {
              try {
                resolve(convertCCPPricingPlansToHAMS(ccpPricingPlans));
              } catch (e) {
                reject(e);
              }
            } else {
              resolve(null);
            }
          }
        )
      );
    });
  }

  private readonly isProductKeyUncached = (productKey: ProductKey): boolean =>
    !this.cache.has(productKey);

  private readonly isOfferingSKUUncached = (
    offeringSKU: OfferingSKU
  ): boolean => !this.cache.has(offeringSKU);

  private readonly getCachedProducts = async (
    productKeys: ReadonlyArray<ProductKey>
  ): Promise<ProductKeyMap<PricingPlans>> => {
    const products = await Promise.all(
      productKeys.map(async (productKey) => {
        const pricingPlans = await this.cache.get(productKey);
        return { productKey, pricingPlans };
      })
    );

    return products
      .filter(({ pricingPlans }) => !!pricingPlans)
      .reduce((result, { productKey, pricingPlans }) => {
        return { ...result, [productKey]: pricingPlans };
      }, {});
  };

  private readonly getCachedOfferings = async (
    offeringSKUs: ReadonlyArray<OfferingSKU>
  ): Promise<OfferingSKUMap<PricingPlans>> => {
    const offerings = await Promise.all(
      offeringSKUs.map(async (offeringSKU) => {
        const pricingPlans = await this.cache.get(offeringSKU);
        return { offeringSKU, pricingPlans };
      })
    );

    return offerings
      .filter(({ pricingPlans }) => !!pricingPlans)
      .reduce((result, { offeringSKU, pricingPlans }) => {
        return { ...result, [offeringSKU]: pricingPlans };
      }, {});
  };
}
